import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "../components/Logo";

const LINKS = [
  { label: "Our Bakes", href: "#gallery", testid: "nav-gallery-link" },
  { label: "Pricing", href: "#pricing", testid: "nav-pricing-link" },
  { label: "Policies", href: "#policies", testid: "nav-policies-link" },
  { label: "About Lall", href: "#about", testid: "nav-about-link" },
  { label: "Contact", href: "#contact", testid: "nav-contact-link" },
];

export const scrollToHash = (hash) => {
  const lenis = window.__lenis;
  const el = document.querySelector(hash);
  if (lenis && el) {
    lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  } else if (el) {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(href);
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-md bg-[#F9F6F0]/85 border-b border-[#8B5D6C]/10 py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <a href="#top" onClick={(e) => go(e, "#top")} data-testid="nav-home-link" aria-label="Velvet Bakes home">
            <Logo />
          </a>
          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.testid}
                onClick={(e) => go(e, l.href)}
                className="text-sm tracking-wide text-[#70505D] hover:text-[#8B5D6C] transition-colors duration-300 relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#C5A059] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
            <a
              href="#order"
              data-testid="nav-order-link"
              onClick={(e) => go(e, "#order")}
              className="text-sm font-medium tracking-wide px-6 py-2.5 rounded-full bg-[#8B5D6C] text-[#F9F6F0] border border-[#C5A059]/60 hover:bg-[#4A2834] transition-colors duration-300"
            >
              Order a Cake
            </a>
          </nav>
          <button
            className="lg:hidden text-[#4A2834] p-2"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-button"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#4A2834]/40 backdrop-blur-sm lg:hidden"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-overlay"
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="absolute right-0 top-0 h-full w-4/5 max-w-sm bg-[#F9F6F0] p-8 flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="nav-mobile-drawer"
            >
              <div className="flex items-center justify-between mb-12">
                <Logo compact />
                <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu" className="p-2 text-[#4A2834]">
                  <X size={24} />
                </button>
              </div>
              <nav className="flex flex-col gap-2">
                {LINKS.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    data-testid={`nav-mobile-${l.testid}`}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 * i + 0.1 }}
                    onClick={(e) => go(e, l.href)}
                    className="font-serif text-3xl text-[#4A2834] py-3 border-b border-[#8B5D6C]/10"
                  >
                    {l.label}
                  </motion.a>
                ))}
              </nav>
              <a
                href="#order"
                data-testid="nav-mobile-order-link"
                onClick={(e) => go(e, "#order")}
                className="mt-auto text-center font-medium tracking-wide px-6 py-4 rounded-full bg-[#8B5D6C] text-[#F9F6F0] border border-[#C5A059]/60"
              >
                Order a Cake
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
